import {BlockedContent} from './BlockedContent';
import {shownName} from './profileTheme';
import React, { useEffect, useState } from 'react';
import { Bookmark, ArrowRight, Trash2 } from 'lucide-react';
import { useLang } from './i18n';

export function SavedMessages({
  request,
  onOpen
}: {
  request: (url: string, method?: string, body?: unknown) => Promise<any>;
  onOpen: (id: string) => void;
}) {
  const [lang, , tr] = useLang();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  function load() {
    request('/bookmarks')
      .then(setData)
      .catch(e => setError(e.message));
  }

  useEffect(() => {
    load();
  }, []);

  async function removeBookmark(e: React.MouseEvent, msgId: string) {
    e.stopPropagation();
    setBusyId(msgId);
    try {
      await request('/bookmarks/' + msgId, 'DELETE');
      setData((prev: any) =>
        prev ? { ...prev, messages: prev.messages.filter((m: any) => m.id !== msgId) } : prev
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="saved-dialog">
      <header className="saved-dialog-head">
        <span className="saved-dialog-icon"><Bookmark size={20} /></span>
        <div>
          <h2>{tr('Saved Messages', 'Gespeicherte Nachrichten')}</h2>
          <p>
            {data
              ? tr(`${data.messages.length} of ${data.limit} slots used`, `${data.messages.length} von ${data.limit} Plätzen belegt`)
              : tr('Loading saved messages…', 'Nachrichten werden geladen …')}
          </p>
        </div>
      </header>
      {data && !data.messages.length ? (
        <div className="saved-empty">
          <Bookmark size={28} />
          <b>{tr('No saved messages yet', 'Noch keine gespeicherten Nachrichten')}</b>
          <p>
            {tr(
              'Right-click a message in any chat and choose Bookmark. It will show up here so you can jump back to it.',
              'Rechtsklick auf eine Nachricht in einem Chat und „Lesezeichen“ wählen. Sie erscheint dann hier und du kannst direkt zurückspringen.'
            )}
          </p>
        </div>
      ) : (
        <div className="saved-messages-list">
          {data?.messages.map((m: any) => (
            <div
              className="saved-message"
              key={m.id}
              onClick={() => onOpen(m.channelId || m.conversationId)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(m.channelId || m.conversationId); } }}
              role="button"
              tabIndex={0}
            >
              <div className="saved-message-info">
                <b>{shownName(m.author) || 'User'}</b>
                <BlockedContent authorId={m.authorId||m.author?.id}><p>{m.body || m.file?.name || tr('Attachment', 'Anhang')}</p></BlockedContent>
                <small>{new Date(m.created).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US')}</small>
              </div>
              <div className="saved-message-actions">
                <button
                  type="button"
                  className="saved-delete-btn"
                  title={tr('Remove from Saved', 'Aus Gespeichert entfernen')}
                  aria-label={tr('Remove from Saved', 'Aus Gespeichert entfernen')}
                  disabled={busyId === m.id}
                  onClick={e => removeBookmark(e, m.id)}
                >
                  <Trash2 size={15} />
                </button>
                <ArrowRight size={17} />
              </div>
            </div>
          ))}
        </div>
      )}
      {error && <p className="form-error">{error}</p>}
    </div>
  );
}
